import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { ResetPasswordModel } from '../../models';
import { AuthService } from '../../services/auth.service';
import { Constants } from '../../services/constants';
import { NavigationService } from 'src/app/services/navigation.service';
import { finalize, map, Subscription } from 'rxjs';
import { GlobalSpinnerService } from 'src/app/shared/services/global-spinner.service';
import { debounce } from 'lodash';
import { AuthenticationUserCompany } from 'src/app/models/auth/authentication-response.model';
import { PasswordPolicyResponse } from 'src/app/models/auth/password-policy-response.model';
import { requireDigitValidator } from 'src/app/shared/directives/validators/require-digit-validator.directive';
import { requireNonAplhanumericValidator } from 'src/app/shared/directives/validators/require-non-aplhanumeric-validator.directive';
import { caseSizeValidator } from 'src/app/shared/directives/validators/case-validator.directive';

@Component({
    selector: 'reset-password',
    templateUrl: './reset-password.component.html',
    standalone: false
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  formData: ResetPasswordModel = new ResetPasswordModel();
  resetPasswordForm: UntypedFormGroup;

  error: string = null;
  upper = 'upper'
  resetDone: boolean = false;
  resettingPassword: boolean = false;

  possibleLogoUrls: string[] = [];

  currentYear: number = new Date().getFullYear();

  availableCompanies: AuthenticationUserCompany[] = [];

  passwordPolicy: PasswordPolicyResponse;

  private _activatedRouteSubscription: Subscription;

  constructor(
    private readonly _authService: AuthService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _navigationService: NavigationService,
    private readonly _spinnerService: GlobalSpinnerService,
  ) {
    this.possibleLogoUrls = this._navigationService.possibleLogoUrls;
  }

  ngOnInit(): void {
    this._activatedRouteSubscription = this._activatedRoute.queryParams.subscribe((params) => {
      const token = params[Constants.authorization.token];
      const email = params[Constants.authorization.userName];
      if (!token) {
        return this._navigationService.navigateToLogin();
      }
      this.formData.code = token;
      this.formData.email = email;

      if (this.formData.email) {
        this.getAvailableCompanies();
      }

      if (this.formData.userCompanyGuid) {
        this.loadPasswordPolicy(this.formData.userCompanyGuid);
      }
    });

    this.resetPasswordForm = new UntypedFormGroup({
      email: new UntypedFormControl(this.formData.email),
      password: new UntypedFormControl(this.formData.password),
      confirmPassword: new UntypedFormControl(this.formData.confirmPassword),
      userCompanyGuid: new UntypedFormControl(this.formData.userCompanyGuid),
    }, { validators: (group: AbstractControl) => this._matchStringValidator(group, 'password', 'confirmPassword') });
  }

  onEmailChangedDebounce = debounce(this.onEmailChanged, 300);

  private onEmailChanged() {
    if (!this.email.valid) {
      this.formData.userCompanyGuid = null;
      return;
    }

    this.getAvailableCompanies();
  }

  // form controls
  get email() { return this.resetPasswordForm.get('email'); }
  get password() { return this.resetPasswordForm.get('password'); }
  get confirmPassword() { return this.resetPasswordForm.get('confirmPassword'); }
  get userCompanyGuid() { return this.resetPasswordForm.get('userCompanyGuid'); }

  resetPassword = () => {
    this.resetPasswordForm.markAllAsTouched();
    if (!this.resetPasswordForm.valid) return;

    this.error = null;
    this.resettingPassword = true;

    this._authService.resetPassword(this.formData)
      .pipe(finalize(() => this.resettingPassword = false))
      .subscribe({
        next: (result) => {
          this.resetDone = true;
        },
        error: (error) => {
          this.error = this._errorMsg(error);
          this.resetDone = false;
        }
      })
  };

  ngOnDestroy(): void {
    this._activatedRouteSubscription?.unsubscribe();
  }

  private _errorMsg(error): string {
    const errorBody = typeof error === 'string' ? error : (error?.error ?? error?.message ?? '');
    const errorText = typeof errorBody === 'string' ? errorBody : (errorBody?.message ?? '');

    if (errorText.indexOf("InvalidToken") > -1) {
      return "Your password reset link has expired or is invalid. Please request a new one.";
    }

    if (errorText) {
      return errorText;
    }

    return "Passwords must contain an uppercase, lowercase, non-alphanumeric character and require at least one digit.";
  }

  private _matchStringValidator = (group: AbstractControl, nameOne: string, nameTwo: string): ValidationErrors | null => {
    const firstValue = group.get(nameOne)?.value;
    const secondValue = group.get(nameTwo)?.value;
    return !firstValue || !secondValue || firstValue === secondValue ? null : { stringDoesntMatch: true }
  }

  onCompanyChanged(): void {
    if (this.formData.userCompanyGuid) {
      this.loadPasswordPolicy(this.formData.userCompanyGuid);
    }
  }

  private loadPasswordPolicy(userCompanyGuid: string): void {
    this._authService.getPasswordPolicy(userCompanyGuid).subscribe({
      next: (policy) => {
        this.passwordPolicy = policy;
        this.applyPasswordValidators(policy);
      }
    });
  }

  private applyPasswordValidators(policy: PasswordPolicyResponse): void {
    const validators: ValidatorFn[] = [Validators.required, Validators.minLength(policy.minimumPasswordLength || 8)];

    if (policy.requireUppercase && policy.requireLowercase) {
      validators.push(caseSizeValidator('both'));
    } else if (policy.requireUppercase) {
      validators.push(caseSizeValidator('upper'));
    } else if (policy.requireLowercase) {
      validators.push(caseSizeValidator('lower'));
    }

    if (policy.requireDigit) {
      validators.push(requireDigitValidator());
    }

    if (policy.requireSpecialCharacter) {
      validators.push(requireNonAplhanumericValidator());
    }

    this.password.setValidators(validators);
    this.password.updateValueAndValidity();
  }

  private getAvailableCompanies = () => {
    this._spinnerService.show();
    this._authService.getAvailableCompanies(this.formData.email).subscribe({
      next: (response) => {
        this.availableCompanies = response || [];

        if (this.availableCompanies.length === 1) {
          this.formData.userCompanyGuid = this.availableCompanies[0].userCompanyGuid;
          this.loadPasswordPolicy(this.availableCompanies[0].userCompanyGuid);
        }
      },
      error: (error) => {
        this.error = this._errorMsg(error.error?.detail || error.error?.message || "Unable to get companies for a provided email");
      }
    }).add(() => {
      this._spinnerService.hide();
    });
  }
}
